import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../components/ui/PageHeader';

// Status color mapping
const STATUS_COLORS = {
  STARTED: 'bg-slate-100 border-slate-300',
  SENT: 'bg-blue-100 border-blue-300',
  IN_PROGRESS: 'bg-amber-100 border-amber-300',
  COMPLETED: 'bg-green-100 border-green-300',
  FAILED: 'bg-red-100 border-red-300',
  CANCELLED: 'bg-gray-100 border-gray-300',
  USER_ABORTED: 'bg-orange-100 border-orange-300',
  SETTLED: 'bg-emerald-100 border-emerald-300',
  EVICTED: 'bg-purple-100 border-purple-300',
  DEFAULT: 'bg-gray-100 border-gray-300'
};

// Highlighted statuses that indicate potential issues
const ALERT_STATUSES = ['NO_QUOTE', 'INVALID_STATUS', 'STALED'];

// Regular statuses for the kanban board (in order)
const REGULAR_STATUSES = [
  'SETTLED',
  'EVICTED',
  'FAILED',
  'COMPLETED',
  'CANCELLED',
  'USER_ABORTED',
  'STARTED',
  'SENT',
  'IN_PROGRESS',
];

// Convert status key to readable label
const formatStatusLabel = (status) => {
  return status
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

function SlicerJobs() {
  const navigate = useNavigate();
  const [board, setBoard] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    jobId: '',
    status: ''
  });

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/slicer-job');
      const data = response.data.data || response.data;
      setBoard(data);
    } catch (error) {
      console.error('Error fetching slicer jobs:', error);
      alert('Failed to load slicer jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseJsonSafely = (jsonString) => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  };

  // Get total count for alert statuses
  const getAlertCount = () => {
    return ALERT_STATUSES.reduce((count, status) => {
      return count + (board[status]?.length || 0);
    }, 0);
  };

  // Filter jobs based on search criteria
  const filterJobs = (jobs) => {
    if (!jobs) return [];
    return jobs.filter(job => {
      if (filters.jobId && !job.jobId?.toLowerCase().includes(filters.jobId.toLowerCase())) {
        return false;
      }
      return true;
    });
  };

  // Get filtered statuses based on status filter
  const getFilteredStatuses = () => {
    if (filters.status) {
      return REGULAR_STATUSES.filter(s => s === filters.status);
    }
    return REGULAR_STATUSES;
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  const alertCount = getAlertCount();

  return (
    <div className="p-6">
      <div className="mb-6">
        <PageHeader title="Slicer Jobs Monitor" />

        {/* Alert Section for NO_QUOTE and INVALID_STATUS */}
        {alertCount > 0 && (
          <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldExclamationIcon className="h-6 w-6 text-red-600" />
              <h2 className="text-lg font-bold text-red-800">
                Security / Bug Alert ({alertCount} jobs)
              </h2>
            </div>
            <p className="text-sm text-red-700 mb-4">
              The following jobs require attention. They may indicate a security breach or a bug in the system.
            </p>

            <div className="grid gap-4 md:grid-cols-2">
              {ALERT_STATUSES.map((status) => {
                const jobs = board[status] || [];
                if (jobs.length === 0) return null;

                return (
                  <div key={status} className="bg-white rounded-lg border border-red-200 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                      <h3 className="font-semibold text-red-800">
                        {formatStatusLabel(status)} ({jobs.length})
                      </h3>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">
                      {status === 'NO_QUOTE'
                        ? 'Jobs without an associated quote reference'
                        : status === 'INVALID_STATUS'
                          ? 'Jobs with an invalid status value'
                          : 'Jobs that have been inactive for too long'}
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {jobs.map((job) => (
                        <div
                          key={job._id}
                          className="bg-red-50 border border-red-200 rounded p-3 text-sm cursor-pointer hover:bg-red-100 transition-colors"
                          onClick={() => navigate(`/slicer-jobs/${job._id}`)}
                        >
                          <div className="flex justify-between items-center mb-1">
                            <div className="font-mono text-xs text-gray-700 truncate">
                              {job.jobId}
                            </div>
                            <span className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                              View →
                            </span>
                          </div>
                          <div className="flex justify-between text-xs text-gray-600">
                            <span>Status: {job.status}</span>
                            <span>{formatDate(job.createdAt)}</span>
                          </div>
                          {job.lastResponse && (
                            <div className="mt-2 text-xs text-gray-500 truncate">
                              Last: {job.lastResponse}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No Alerts Message */}
        {alertCount === 0 && (
          <div className="mb-6 bg-green-50 border border-green-300 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <ShieldExclamationIcon className="h-5 w-5 text-green-600" />
              <span className="text-green-800 font-medium">
                No alerts
              </span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Filter by Job ID..."
              value={filters.jobId}
              onChange={(e) => setFilters({ ...filters, jobId: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="w-48">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              {REGULAR_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {formatStatusLabel(status)}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={fetchJobs}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {getFilteredStatuses().map((statusKey) => {
          const statusColor = STATUS_COLORS[statusKey] || STATUS_COLORS.DEFAULT;
          const statusLabel = formatStatusLabel(statusKey);
          const filteredJobs = filterJobs(board[statusKey]);

          return (
            <div key={statusKey} className="flex-shrink-0 w-80">
              {/* Column Header */}
              <div className={`${statusColor} border-2 rounded-t-lg px-4 py-3`}>
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-lg">{statusLabel}</h2>
                  <span className="bg-white px-2 py-1 rounded-full text-sm font-medium">
                    {filteredJobs.length}
                  </span>
                </div>
              </div>

              {/* Column Body */}
              <div className="bg-gray-50 border-2 border-t-0 rounded-b-lg min-h-96 p-3 space-y-3">
                {filteredJobs.map((job) => {
                  const stats = parseJsonSafely(job.stats);

                  return (
                    <div
                      key={job._id}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
                    >
                      {/* Job Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          {job.file?.name && (
                            <div className="font-medium text-gray-900 text-sm truncate mb-1" title={job.file.name}>
                              {job.file.name}
                            </div>
                          )}
                          <div className="font-mono text-xs text-gray-500 truncate" title={job.jobId}>
                            {job.jobId?.substring(0, 8)}...
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            {formatDate(job.createdAt)}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 ml-2 flex-shrink-0">
                          {job.is3MF && (
                            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                              3MF
                            </span>
                          )}
                          {job.plateCount > 1 && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded flex items-center gap-1">
                              <CubeIcon className="h-3 w-3" />
                              {job.plateCount} plates
                            </span>
                          )}
                          {job.quote && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                              Has Quote
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Settings Summary */}
                      {job.settings && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {job.settings.infill !== undefined && (
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                              Infill {job.settings.infill}%
                            </span>
                          )}
                          {job.settings.walls !== undefined && (
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                              Walls {job.settings.walls}
                            </span>
                          )}
                          {job.settings.support !== undefined && (
                            <span className={`text-xs px-2 py-0.5 rounded ${job.settings.support ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              Support {job.settings.support ? 'On' : 'Off'}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Job Details */}
                      <div className="space-y-2 mb-3 text-sm">
                        {job.execCode !== undefined && job.execCode !== 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Exit Code:</span>
                            <span className="font-medium text-red-600">{job.execCode}</span>
                          </div>
                        )}
                        {stats?.printTime && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Print Time:</span>
                            <span className="font-medium">
                              {Math.round(stats.printTime / 60)} min
                            </span>
                          </div>
                        )}
                        {stats?.filamentUsed && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Filament:</span>
                            <span className="font-medium">{stats.filamentUsed}g</span>
                          </div>
                        )}
                        {job.jobFiles?.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Files:</span>
                            <span className="font-medium">{job.jobFiles.length}</span>
                          </div>
                        )}
                      </div>

                      {/* Warnings */}
                      {job.warnings && (
                        <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                          <div className="font-medium text-yellow-800 mb-1">Warnings:</div>
                          <div className="text-yellow-700 line-clamp-2">
                            {job.warnings}
                          </div>
                        </div>
                      )}

                      {/* Last Response */}
                      {job.lastResponse && (
                        <div className="p-2 bg-gray-50 border border-gray-200 rounded text-xs">
                          <div className="flex items-center gap-1 text-gray-600 mb-1">
                            <ClockIcon className="h-3 w-3" />
                            <span>Last Response:</span>
                          </div>
                          <div className="text-gray-700 line-clamp-2">
                            {job.lastResponse}
                          </div>
                          {job.lastResponseAt && (
                            <div className="text-gray-500 mt-1">
                              {formatDate(job.lastResponseAt)}
                            </div>
                          )}
                        </div>
                      )}

                      {/* S3 Key Info */}
                      {job.s3Key && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <DocumentTextIcon className="h-3 w-3" />
                            <span className="truncate" title={job.s3Key}>
                              {job.s3Key.split('/').pop()}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* View Details Button */}
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <button
                          onClick={() => navigate(`/slicer-jobs/${job._id}`)}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                          <EyeIcon className="h-4 w-4" />
                          View Details
                        </button>
                      </div>
                    </div>
                  );
                })}

                {filteredJobs.length === 0 && (
                  <div className="text-center text-gray-400 py-8">
                    <div className="text-sm">No jobs in this status</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SlicerJobs;
